import { ProposalContent, ProposalImages, AdditionalPhoto, TimelineItem, CabinetOption, SavedProposal } from "@/types/proposal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, FolderOpen, Download, FileText, Printer, X, Plus, Settings, Link } from "lucide-react";
import { EmailProposalDialog } from "./EmailProposalDialog";
import { SignedProposalsDialog } from "./SignedProposalsDialog";
import { LogoutButton } from "./LogoutButton";
import { EmailSummaryBox } from "./EmailSummaryBox";
import { useNavigate } from "react-router-dom";
import { Session } from "@supabase/supabase-js";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface BuilderPanelProps {
  content: ProposalContent;
  images: ProposalImages;
  additionalPhotos: AdditionalPhoto[];
  currentProposalName: string;
  session: Session | null;
  savedProposals: SavedProposal[];
  onContentChange: (field: keyof ProposalContent, value: any) => void;
  onImageUpload: (imageType: keyof ProposalImages, event: React.ChangeEvent<HTMLInputElement>) => void;
  onOptionImageUpload: (optionId: string, type: 'main' | 'vanity', file: File) => void;
  onAdditionalPhotoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onInspirationPhotoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAdditionalPhoto: (id: number) => void;
  onUpdatePhotoCaption: (id: number, caption: string) => void;
  onTogglePhotoVisibility: (id: number) => void;
  onTimelineChange: (index: number, field: keyof TimelineItem, value: string) => void;
  onNextStepChange: (index: number, value: string) => void;
  onAddTimelineItem: () => void;
  onRemoveTimelineItem: (index: number) => void;
  onAddNextStep: () => void;
  onRemoveNextStep: (index: number) => void;
  onSave: () => void;
  onLoad: () => void;
  onExport: () => void;
  onClientView: () => void;
  onPrint: () => void;
  onViewSignedProposal: (proposal: any) => void;
  onExportSignedPDF: (proposal: any) => void;
}

export const BuilderPanel = ({
  content,
  images,
  additionalPhotos,
  currentProposalName,
  session,
  savedProposals,
  onContentChange,
  onImageUpload,
  onOptionImageUpload,
  onAdditionalPhotoUpload,
  onInspirationPhotoUpload,
  onRemoveAdditionalPhoto,
  onUpdatePhotoCaption,
  onTogglePhotoVisibility,
  onTimelineChange,
  onNextStepChange,
  onAddTimelineItem,
  onRemoveTimelineItem,
  onAddNextStep,
  onRemoveNextStep,
  onSave,
  onLoad,
  onExport,
  onClientView,
  onPrint,
  onViewSignedProposal,
  onExportSignedPDF,
}: BuilderPanelProps) => {
  const navigate = useNavigate();

  const handleNumberOfOptionsChange = (value: string) => {
    const num = parseInt(value);
    const currentOptions = content.cabinetOptions || [];
    
    // Add or remove options to match the selected number
    if (num > currentOptions.length) {
      const newOptions = [...currentOptions];
      for (let i = currentOptions.length; i < num; i++) {
        newOptions.push({
          id: `option-${i + 1}`,
          title: `Option ${i + 1}`,
          description: "Description for this cabinet style...",
          price: "",
          vanityTitle: `Option ${i + 1} Vanity`,
          vanityDescription: "Description for this vanity style...",
        });
      }
      onContentChange('cabinetOptions', newOptions);
    } else if (num < currentOptions.length) {
      onContentChange('cabinetOptions', currentOptions.slice(0, num));
    }
    onContentChange('numberOfOptions', num);
  };

  const handleOptionChange = (index: number, field: keyof CabinetOption, value: string) => {
    const newOptions = [...content.cabinetOptions];
    newOptions[index] = { ...newOptions[index], [field]: value };
    onContentChange('cabinetOptions', newOptions);
  };

  const handleOptionImageUpload = (optionId: string, type: 'main' | 'vanity', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onOptionImageUpload(optionId, type, file);
    }
  };

  return (
    <div className="bg-white shadow-lg border-b border-slate-200 print:hidden relative z-50">
      <div className="max-w-7xl mx-auto">
        {/* Top Menu Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-8 py-4 border-b border-slate-100 gap-3 sticky top-0 z-50 bg-white">
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => navigate("/admin")} variant="outline" size="sm" className="text-xs sm:text-sm">
              <Settings className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Admin Settings</span>
            </Button>
            <LogoutButton />
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-end">
            <Button onClick={onSave} size="sm" className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none">
              <Save className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="text-xs sm:text-sm">Save</span>
            </Button>
            <Button onClick={onLoad} size="sm" variant="secondary" className="flex-1 sm:flex-none">
              <FolderOpen className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="text-xs sm:text-sm">Load</span>
            </Button>
            <SignedProposalsDialog 
              onViewProposal={onViewSignedProposal}
              onExportSignedPDF={onExportSignedPDF}
            />
            <EmailProposalDialog 
              content={content} 
              images={images} 
              additionalPhotos={additionalPhotos} 
              creatorUserId={session?.user.id || ''}
            />
            <Button 
              onClick={() => {
                const proposalParam = currentProposalName ? `&proposal=${encodeURIComponent(currentProposalName)}` : '';
                const clientUrl = `${window.location.origin}/?client=true&creator=${session?.user.id}${proposalParam}`;
                navigator.clipboard.writeText(clientUrl);
                toast.success("Client link copied to clipboard!");
              }} 
              size="sm" 
              variant="outline"
              className="flex-1 sm:flex-none"
            >
              <Link className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm">Copy Link</span>
            </Button>
            <Button onClick={onExport} size="sm" className="bg-orange-600 hover:bg-orange-700 flex-1 sm:flex-none">
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm">Export</span>
            </Button>
            <Button onClick={onClientView} size="sm" className="bg-purple-600 hover:bg-purple-700 flex-1 sm:flex-none">
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm">View</span>
            </Button>
            <Button onClick={onPrint} size="sm" className="flex-1 sm:flex-none" title="Print current view (Ctrl/Cmd + P)">
              <Printer className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline text-xs sm:text-sm">Print</span>
            </Button>
          </div>
        </div>

        {/* Title Section */}
        <div className="px-4 sm:px-8 py-3">
          <h1 className="text-lg sm:text-2xl font-light text-slate-800 tracking-tight">
            Foundry Cabinet Co. by District Design Build, LLC
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Professional Proposal Builder</p>
          {currentProposalName && (
            <div className="text-xs sm:text-sm text-slate-500 mt-1">
              Current: <span className="font-medium">{currentProposalName}</span>
              <span className="ml-2 text-green-600">● Auto-saving</span>
            </div>
          )}
        </div>

        <div className="px-4 sm:px-8 py-6">
          {/* Email Summary Box */}
          <EmailSummaryBox 
            savedProposals={savedProposals} 
            userId={session?.user?.id}
            clientName={content.clientName}
          />
          
          {/* Visual Assets Accordion - Above */}
          <div className="mb-6">
            <Accordion type="single" collapsible defaultValue="visual-assets" className="w-full">
              <AccordionItem value="visual-assets" className="border rounded-lg px-3 sm:px-4">
                <AccordionTrigger className="text-base sm:text-xl font-medium text-slate-800 tracking-tight hover:no-underline">
                  Visual Assets
                </AccordionTrigger>
                <AccordionContent className="pt-2">
                  <div className="space-y-6">
                    {/* Logo */}
                    <div>
                      <Label className="text-xs sm:text-sm font-medium text-slate-700 mb-2">Company Logo</Label>
                      <Input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => onImageUpload('logo', e)} 
                        className="text-xs sm:text-sm"
                      />
                    </div>

                    <div className="pt-4 border-t">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-xs text-blue-800">
                          💡 <strong>Tip:</strong> Drag photos from the galleries below directly onto the cabinet option frames in the proposal preview to add images.
                        </p>
                      </div>
                      
                      {/* Project Photos */}
                      <div className="mb-6">
                        <Label className="text-xs sm:text-sm font-medium text-slate-700 mb-2">Project Photos (Shown to Client)</Label>
                        <Input 
                          type="file" 
                          accept="image/*" 
                          multiple 
                          onChange={onAdditionalPhotoUpload} 
                          className="mb-2 text-xs sm:text-sm"
                        />
                        <p className="text-xs text-slate-500">Upload photos to display in client proposal</p>
                      </div>

                      {/* Inspiration Gallery */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <Label className="text-xs sm:text-sm font-medium text-amber-900 mb-2">Inspiration Gallery (Reference Only)</Label>
                        <Input 
                          type="file" 
                          accept="image/*" 
                          multiple 
                          onChange={onInspirationPhotoUpload} 
                          className="mb-2 text-xs sm:text-sm"
                        />
                        <p className="text-xs text-amber-700">📌 Upload Pinterest/reference images for internal use (hidden from client by default)</p>
                      </div>

                      {/* Photo Management */}
                      {additionalPhotos.length > 0 && (
                        <div className="pt-6 border-t mt-6">
                          <Label className="text-xs sm:text-sm font-medium text-slate-700 mb-3 block">Manage Photos</Label>
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {additionalPhotos.map((photo) => (
                              <div 
                                key={photo.id} 
                                className={`flex items-center gap-3 p-3 rounded-lg border-2 ${
                                  photo.isInspiration 
                                    ? 'bg-amber-50 border-amber-200' 
                                    : 'bg-slate-50 border-slate-200'
                                }`}
                              >
                                <img src={photo.dataUrl} alt={photo.caption} className="w-16 h-16 object-cover rounded" />
                                <div className="flex-1 min-w-0">
                                  <Input
                                    value={photo.caption}
                                    onChange={(e) => onUpdatePhotoCaption(photo.id, e.target.value)}
                                    className="text-xs mb-1"
                                    placeholder="Photo caption..."
                                  />
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className={`font-medium ${photo.isInspiration ? 'text-amber-700' : 'text-slate-600'}`}>
                                      {photo.isInspiration ? '📌 Inspiration' : '📸 Project'}
                                    </span>
                                    <span className="text-slate-400">•</span>
                                    <label className="flex items-center gap-1 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={photo.showInClient !== false}
                                        onChange={() => onTogglePhotoVisibility(photo.id)}
                                        className="w-3 h-3"
                                      />
                                      <span className="text-slate-600">Show to client</span>
                                    </label>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onRemoveAdditionalPhoto(photo.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Proposal Content - Below */}
          <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 space-y-6">
            <h3 className="text-base sm:text-lg font-medium text-slate-800">Proposal Content</h3>

            {/* Number of Options Selector */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Label className="text-sm font-medium text-slate-800 mb-2 block">Number of Cabinet Options to Show</Label>
              <Select 
                value={content.numberOfOptions.toString()} 
                onValueChange={handleNumberOfOptionsChange}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-50">
                  <SelectItem value="1">1 Option</SelectItem>
                  <SelectItem value="2">2 Options</SelectItem>
                  <SelectItem value="3">3 Options</SelectItem>
                  <SelectItem value="4">4 Options</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-600 mt-2">Choose how many different cabinet styles to present to the client</p>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <Label>Project Name</Label>
                <Input
                  value={content.projectName}
                  onChange={(e) => onContentChange('projectName', e.target.value)}
                  placeholder="e.g., Forest Grove Kitchen Remodel"
                />
              </div>
              <div>
                <Label>Client Name</Label>
                <Input
                  value={content.clientName}
                  onChange={(e) => onContentChange('clientName', e.target.value)}
                />
              </div>
              <div>
                <Label>Project Number</Label>
                <Input
                  value={content.projectNumber}
                  onChange={(e) => onContentChange('projectNumber', e.target.value)}
                />
              </div>
              <div>
                <Label>Company Address</Label>
                <Input
                  value={content.companyAddress}
                  onChange={(e) => onContentChange('companyAddress', e.target.value)}
                />
              </div>
              <div>
                <Label>Company Phone</Label>
                <Input
                  value={content.companyPhone}
                  onChange={(e) => onContentChange('companyPhone', e.target.value)}
                />
              </div>
              <div>
                <Label>Company Email</Label>
                <Input
                  type="email"
                  value={content.companyEmail}
                  onChange={(e) => onContentChange('companyEmail', e.target.value)}
                />
              </div>
              <div>
                <Label>Company Website</Label>
                <Input
                  value={content.companyWebsite}
                  onChange={(e) => onContentChange('companyWebsite', e.target.value)}
                />
              </div>
              <div>
                <Label>Online Plans Link</Label>
                <Input
                  type="url"
                  value={content.onlinePlansLink}
                  onChange={(e) => onContentChange('onlinePlansLink', e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>SketchUp 3D Model Link</Label>
                <Input
                  type="url"
                  value={content.sketchupLink}
                  onChange={(e) => onContentChange('sketchupLink', e.target.value)}
                  placeholder="https://3dwarehouse.sketchup.com/..."
                />
              </div>
              <div>
                <Label>Total Investment</Label>
                <Input
                  value={content.totalInvestment}
                  onChange={(e) => onContentChange('totalInvestment', e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label>Executive Summary</Label>
              <Textarea
                value={content.executiveSummary}
                onChange={(e) => onContentChange('executiveSummary', e.target.value)}
                rows={4}
              />
            </div>

            {/* Cabinet Options */}
            <div className="space-y-4 border-t pt-6">
              <h4 className="text-base font-medium text-slate-800">Cabinet Style Options</h4>
              {content.cabinetOptions.map((option, index) => (
                <div key={option.id} className="border rounded-lg p-4 bg-slate-50 space-y-3">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-slate-800">Option {index + 1}</h5>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Cabinet Style Title</Label>
                      <Input
                        value={option.title}
                        onChange={(e) => handleOptionChange(index, 'title', e.target.value)}
                        placeholder="e.g., Shaker Style"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Price (Optional)</Label>
                      <Input
                        value={option.price || ''}
                        onChange={(e) => handleOptionChange(index, 'price', e.target.value)}
                        placeholder="e.g., $12,500 or Contact for Quote"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Cabinet Description</Label>
                      <Textarea
                        value={option.description}
                        onChange={(e) => handleOptionChange(index, 'description', e.target.value)}
                        rows={2}
                        placeholder="Describe the cabinet style..."
                      />
                    </div>
                  </div>

                  {/* Vanity Section - Optional for bathroom projects */}
                  <div className="pt-4 border-t border-slate-200">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                      <p className="text-xs text-amber-900 font-medium mb-1">🚿 Vanity Section (Optional)</p>
                      <p className="text-xs text-amber-700">Only fill this out if the project includes bathroom cabinets. Leave empty for kitchen-only projects.</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Vanity Title</Label>
                        <Input
                          value={option.vanityTitle || ''}
                          onChange={(e) => handleOptionChange(index, 'vanityTitle', e.target.value)}
                          placeholder="e.g., Option 1 Vanity"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Vanity Description</Label>
                        <Textarea
                          value={option.vanityDescription || ''}
                          onChange={(e) => handleOptionChange(index, 'vanityDescription', e.target.value)}
                          rows={2}
                          placeholder="Describe the vanity style..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Timeline */}
            <div className="border-t pt-6">
              <div className="flex justify-between items-center mb-4">
                <Label>Project Timeline</Label>
                <Button onClick={onAddTimelineItem} size="sm" variant="outline">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Phase
                </Button>
              </div>
              <div className="space-y-3">
                {content.timeline.map((item, index) => (
                  <div key={index} className="flex gap-3 items-center">
                    <Input
                      value={item.period}
                      onChange={(e) => onTimelineChange(index, 'period', e.target.value)}
                      placeholder="Period"
                      className="flex-1"
                    />
                    <Input
                      value={item.task}
                      onChange={(e) => onTimelineChange(index, 'task', e.target.value)}
                      placeholder="Task"
                      className="flex-1"
                    />
                    <Button
                      onClick={() => onRemoveTimelineItem(index)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Steps */}
            <div className="border-t pt-6">
              <div className="flex justify-between items-center mb-4">
                <Label>Next Steps</Label>
                <Button onClick={onAddNextStep} size="sm" variant="outline">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Step
                </Button>
              </div>
              <div className="space-y-3">
                {content.nextSteps.map((step, index) => (
                  <div key={index} className="flex gap-3 items-center">
                    <Input
                      value={step}
                      onChange={(e) => onNextStepChange(index, e.target.value)}
                      placeholder="Next step"
                      className="flex-1"
                    />
                    <Button
                      onClick={() => onRemoveNextStep(index)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};